import { useEffect, useRef, useState, useCallback } from 'react';
import { gsap } from 'gsap';
import { useTransition } from '../context/TransitionContext';
import { slugify } from '../utils/subdomain';
import { extractColors } from '../utils/colorExtractor';
import ShareButton from './ShareButton';

const WorkCard = ({ project, index }) => {
  const { startTransition } = useTransition();
  const cardRef = useRef(null);
  const path1Ref = useRef(null);
  const path2Ref = useRef(null);
  const titleRef = useRef(null);
  const wordsRef = useRef([]);
  const imageRef = useRef(null);
  const overlayRef = useRef(null);
  const detailsRef = useRef(null);
  const [colors, setColors] = useState({ primary: '#FF7722', secondary: '#e0e0e0' });

  useEffect(() => {
    const p1 = path1Ref.current;
    const p2 = path2Ref.current;

    if (p1 && p2) {
      const l1 = p1.getTotalLength();
      const l2 = p2.getTotalLength();

      gsap.set(p1, { strokeDasharray: l1, strokeDashoffset: l1 });
      gsap.set(p2, { strokeDasharray: l2, strokeDashoffset: l2 });
    }

    // Split title words for animation
    if (titleRef.current) {
      const text = titleRef.current.textContent.trim();
      const words = text.split(/\s+/);
      titleRef.current.textContent = '';
      
      words.forEach((word) => {
        const mask = document.createElement('span');
        mask.className = 'word-mask';
        mask.style.display = 'inline-block';
        mask.style.overflow = 'hidden';

        const span = document.createElement('span');
        span.className = 'word';
        span.textContent = word;
        span.style.display = 'inline-block';
        
        mask.appendChild(span);
        titleRef.current.appendChild(mask);
        titleRef.current.appendChild(document.createTextNode(' '));
        wordsRef.current.push(span);
      });

      gsap.set(wordsRef.current, { yPercent: 100 });
    }

    // Set initial state for details
    if (detailsRef.current) {
      gsap.set(detailsRef.current, { opacity: 0, y: 20 });
    }

    return () => {
      wordsRef.current = [];
      if (titleRef.current) {
        const text = Array.from(titleRef.current.querySelectorAll('.word'))
          .map(w => w.textContent)
          .join(' ');
        titleRef.current.textContent = text;
      }
    };
  }, []);

  // Extract colors from project image
  const handleImageLoad = useCallback(() => {
    if (project.image) {
      extractColors(project.image).then(extracted => {
        setColors(extracted);
      });
    }
  }, [project.image]);

  useEffect(() => {
    if (imageRef.current && project.image) {
      if (imageRef.current.complete) {
        handleImageLoad();
      }
    }
  }, [project.image, handleImageLoad]);

  const handleMouseEnter = () => {
    const p1 = path1Ref.current;
    const p2 = path2Ref.current;
    const l1 = p1?.getTotalLength();
    const l2 = p2?.getTotalLength();

    const tl = gsap.timeline();

    // Animate SVG paths
    if (p1 && p2 && l1 && l2) {
      tl.to([p1, p2], {
        strokeDashoffset: 0,
        attr: { 'stroke-width': 700 },
        duration: 1.5,
        ease: 'power2.out',
        stagger: 0,
      }, 0);
    }

    // Animate title words
    tl.to(wordsRef.current, {
      yPercent: 0,
      duration: 0.75,
      ease: 'power3.out',
      stagger: 0.075,
    }, 0.35);

    // Animate image scale
    if (imageRef.current) {
      tl.to(imageRef.current, {
        scale: 1.1,
        duration: 1.2,
        ease: 'power2.out',
      }, 0);
    }

    // Animate overlay
    if (overlayRef.current) {
      tl.to(overlayRef.current, {
        opacity: 0.4,
        duration: 0.8,
        ease: 'power2.out',
      }, 0);
    }

    // Animate details
    if (detailsRef.current) {
      tl.to(detailsRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: 'power3.out',
      }, 0.4);
    }
  };

  const handleMouseLeave = () => {
    const p1 = path1Ref.current;
    const p2 = path2Ref.current;
    const l1 = p1?.getTotalLength();
    const l2 = p2?.getTotalLength();

    const tl = gsap.timeline();

    // Animate SVG paths back
    if (p1 && l1) {
      tl.to(p1, {
        strokeDashoffset: l1,
        attr: { 'stroke-width': 200 },
        duration: 1,
        ease: 'power2.out',
      }, 0);
    }

    if (p2 && l2) {
      tl.to(p2, {
        strokeDashoffset: l2,
        attr: { 'stroke-width': 200 },
        duration: 1,
        ease: 'power2.out',
      }, 0);
    }

    // Animate title words back
    tl.to(wordsRef.current, {
      yPercent: 100,
      duration: 0.5,
      ease: 'power3.out',
      stagger: { each: 0.05, from: 'end' },
    }, 0);

    // Animate image scale back
    if (imageRef.current) {
      tl.to(imageRef.current, {
        scale: 1,
        duration: 1,
        ease: 'power2.out',
      }, 0);
    }

    // Animate overlay back
    if (overlayRef.current) {
      tl.to(overlayRef.current, {
        opacity: 0.6,
        duration: 0.8,
        ease: 'power2.out',
      }, 0);
    }

    // Animate details back
    if (detailsRef.current) {
      tl.to(detailsRef.current, {
        opacity: 0,
        y: 20,
        duration: 0.4,
        ease: 'power3.out',
      }, 0);
    }
  };

  const handleClick = () => {
    startTransition(`/work/${slugify(project.title)}`);
  };

  // Dynamic colors extracted from project image
  const strokeColor1 = colors.primary;
  const strokeColor2 = colors.secondary;

  return (
    <div
      ref={cardRef}
      className="work-card"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      style={{ cursor: 'pointer' }}
    >
      {/* Background Image */}
      <div className="work-card-image">
        <img
          ref={imageRef}
          src={project.image || `https://via.placeholder.com/600x600/171412/8E827C?text=${encodeURIComponent(project.title)}`}
          alt={project.title}
          onLoad={handleImageLoad}
          crossOrigin="anonymous"
        />
      </div>

      {/* SVG Stroke Animation Layer 1 */}
      <div className="work-card-svg work-card-svg-1">
        <svg viewBox="0 0 2453 2535" fill="none" preserveAspectRatio="none">
          <path
            ref={path1Ref}
            d="M227.549 1818.76C227.549 1818.76 406.016 2207.75 569.049 2130.26C843.431 1999.85 -264.104 1002.3 227.549 876.262C552.918 792.849 773.647 2456.11 1342.05 2130.26C1885.43 1818.76 14.9644 455.772 760.548 137.262C1342.05 -111.152 1663.5 2266.35 2209.55 1972.76C2755.6 1679.18 1536.63 384.467 1826.55 137.262C2013.5 -22.1463 2209.55 381.262 2209.55 381.262"
            stroke={strokeColor1}
            strokeWidth="200"
            strokeLinecap="round"
          />
        </svg>
      </div>

      {/* SVG Stroke Animation Layer 2 */}
      <div className="work-card-svg work-card-svg-2">
        <svg viewBox="0 0 2453 2535" fill="none" preserveAspectRatio="none">
          <path
            ref={path2Ref}
            d="M1661.28 2255.51C1661.28 2255.51 2311.09 1960.37 2111.78 1817.01C1944.47 1696.67 718.456 2870.17 499.781 2255.51C308.969 1719.17 2457.51 1613.83 2111.78 963.512C1766.05 313.198 427.949 2195.17 132.281 1455.51C-155.219 736.292 2014.78 891.514 1708.78 252.012C1437.81 -314.29 369.471 909.169 132.281 566.512C18.1772 401.672 244.781 193.012 244.781 193.012"
            stroke={strokeColor2}
            strokeWidth="200"
            strokeLinecap="round"
          />
        </svg>
      </div>

      {/* Overlay */}
      <div 
        ref={overlayRef}
        className="work-card-overlay"
        style={{ opacity: 0.6 }}
      />

      {/* Content */}
      <div className="work-card-content">
        {/* Category Badge */}
        <div className="work-card-category">
          <span>{project.category}</span>
          {project.year && <span className="work-card-year">• {project.year}</span>}
        </div>

        {/* Title */}
        <div className="work-card-title">
          <h3 ref={titleRef}>{project.title}</h3>
        </div>

        {/* Details (animated on hover) */}
        <div ref={detailsRef} className="work-card-details">
          {project.description && (
            <p className="work-card-description">{project.description}</p>
          )}
          
          {/* Tech Stack */}
          {project.tech && project.tech.length > 0 && (
            <div className="work-card-tech">
              {project.tech.slice(0, 4).map((tech, idx) => (
                <span key={idx} className="work-card-tech-badge">
                  {tech}
                </span>
              ))}
              {project.tech.length > 4 && (
                <span className="work-card-tech-badge">
                  +{project.tech.length - 4}
                </span>
              )}
            </div>
          )}

          {/* Action Button */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="work-card-action">
              VIEW PROJECT →
            </div>
            <ShareButton
              title={project.title}
              description={project.description}
              url={project.details_link || window.location.href}
              style={{ width: '32px', height: '32px' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkCard;